import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Card, Badge } from '@edx/paragon';
import { injectIntl, defineMessages } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import setActiveCourseRun from '@data/actions/activeCourseRun';
import { getCourseRunTilePrimaryText, getCourseRunTileSecondaryText } from '@utils/courseRunSelection';
import courseRunShape from '@prospectus/common/types/courseRunShape';

import EnrollButton from '../../EnrollButton/EnrollButtonCommon';

const messages = defineMessages({
  'prospectus.course.runSelector.tile.pill.selfPaced': {
    id: 'prospectus.course.runSelector.tile.pill.selfPaced',
    defaultMessage: 'Self-paced',
    description: 'This tells a user that the course will be self-paced, not paced by an instructor',
  },
  'prospectus.course.runSelector.tile.pill.instructorPaced': {
    id: 'prospectus.course.runSelector.tile.pill.instructorPaced',
    defaultMessage: 'Instructor-paced',
    description: 'This tells a user that the course will be paced by an instructor, not self-paced',
  },
  'prospectus.course.runSelector.tile.enroll': {
    id: 'prospectus.course.runSelector.tile.enroll',
    defaultMessage: 'Enroll',
    description: 'This is the label on an enroll button that will enroll the user in the course',
  },
});

const mapPacingToText = {
  self_paced: 'prospectus.course.runSelector.tile.pill.selfPaced',
  instructor_paced: 'prospectus.course.runSelector.tile.pill.instructorPaced',
};

function BaseSelectionTile({
  uuid,
  intl,
  courseRun,
  courseRun: {
    pacingType,
  },
  emailOptIn,
  includePacing,
  enrolledInOneRun,
  owners,
  cohesionVariant,
  cohesionCategory,
  cohesionName,
}) {
  const secondaryText = getCourseRunTileSecondaryText(courseRun, intl);

  return (
    <Card style={{ width: '100%' }}>
      <Card.Body className="card-body">
        <div className="d-flex h-100 flex-column align-items-center justify-content-between" data-test-id={courseRun.key}>
          {includePacing
          && (
            <Badge variant="light" className="mb-3">
              {intl.formatMessage(messages[mapPacingToText[pacingType]])}
            </Badge>
          )}

          <div className="text-center mb-3.5">
            <div className="h4 mb-0">
              { getCourseRunTilePrimaryText(courseRun, intl) }
            </div>
            {secondaryText
              && (
                <div className="course-end-text small">
                  { secondaryText }
                </div>
              )}
          </div>
          <EnrollButton
            uuid={uuid}
            product="course"
            className="w-100 main-enroll-btn"
            buttonClass="w-100"
            emailOptIn={emailOptIn}
            courseRun={courseRun}
            enrolledInOneRun={enrolledInOneRun}
            owners={owners}
            cohesionVariant={cohesionVariant}
            cohesionCategory={cohesionCategory}
            cohesionName={cohesionName}
          />
        </div>
      </Card.Body>
    </Card>
  );
}

BaseSelectionTile.propTypes = {
  intl: intlShape.isRequired,
  courseRun: PropTypes.shape(courseRunShape).isRequired,
  uuid: PropTypes.string.isRequired,
  emailOptIn: PropTypes.bool,
  includePacing: PropTypes.bool,
  enrolledInOneRun: PropTypes.bool.isRequired,
  owners: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  cohesionVariant: PropTypes.string,
  cohesionCategory: PropTypes.string,
  cohesionName: PropTypes.string,
};

BaseSelectionTile.defaultProps = {
  emailOptIn: false,
  includePacing: false,
  owners: [],
  cohesionVariant: null,
  cohesionCategory: null,
  cohesionName: null,
};

const mapStateToProps = store => ({
  activeCourseRun: store.activeCourseRun,
});

const mapDispatchToProps = dispatch => ({
  onSetActiveCourseRun(activeCourseRun) {
    dispatch(setActiveCourseRun(activeCourseRun));
  },
});

const SelectionTile = connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(BaseSelectionTile));

export default SelectionTile;
